import React from 'react'

import { useQuery } from '@apollo/client'

import ContentPlaceholder from '~/components/ContentPlaceholder'
import DocumentPreviewCard from '~/components/DocumentPreviewCard'
import ObservationWebViewCard from '~/components/ObservationWebViewCard'
import { isWebView } from '~/config'

import { useSelectedServiceFkey } from '~/contexts/Service'
import { useCaptureUnexpectedApolloError } from '~/hooks/useCaptureUnexpectedApolloError'
import { is401, is404 } from '~/modules/api'
import { GENERIC_PREVIEW_ERROR_DESCRIPTION } from '~/modules/errors'
import { GET_POST, GET_POST_TEXT } from '~/pages/Observations/queries'

interface LoaderProps {
  id: number
}
interface Props {
  post: Playground.Post
}

const ObservationPreviewCard = React.memo(({ post }: Props) => {
  return (
    <>
      {isWebView ? (
        <ObservationWebViewCard post={post} />
      ) : (
        <DocumentPreviewCard
          description={post?.description}
          title={post?.title}
          onOpen={() => window.open(`/observations/${post.id}`)}
        />
      )}
    </>
  )
})

const ObservationLoader = ({ id }: LoaderProps) => {
  const serviceFkey = useSelectedServiceFkey()

  const query = isWebView ? GET_POST : GET_POST_TEXT

  const { data, error, loading } = useQuery(query, {
    variables: { id, serviceFkey },
    skip: !id,
  })

  useCaptureUnexpectedApolloError(error, {
    component: 'ObservationPreviewCard',
    skip: is404(error) || is401(error),
  })

  if (error) {
    // 404 and 401 are expected when embedding an observation — the parent may not have a child tagged in it
    if (is404(error) || is401(error)) {
      return (
        <DocumentPreviewCard
          title="Post"
          description="You do not have permission to view this content. Your child is not tagged in the post."
        />
      )
    }

    return <DocumentPreviewCard title="Post" description={GENERIC_PREVIEW_ERROR_DESCRIPTION} />
  }

  const post = data?.context?.post as Playground.Post

  if (loading || !post) return <ContentPlaceholder />
  if (post.status === 'DELETED') return null

  return <ObservationPreviewCard post={post} />
}

ObservationPreviewCard.displayName = 'ObservationPreviewCard'
ObservationLoader.displayName = 'ObservationLoader'

export default React.memo(ObservationLoader)
